import { ID_QUERY } from './common';
import { format } from 'date-fns';
import { Tag, TagResItem } from './tag';
import { Category, CategoryResItem } from './category';
import { Cta, CtaResItem, CTA_ITEM_QUERY } from './cta';

export interface ArticleResItem {
  sys: {
    id: string;
  };
  slug: string;
  title: string;
  author: string;
  published: string;
  primaryImageCaption: string;
  primaryVideo: string;
  primaryVideoCaption: string;
  content: string;
  shortOverview: string;
  readTime: number;
  metaDescription: string;
  metaTitle: string;
  primaryImage: {
    url: string;
  };
  category: CategoryResItem;
  tagCollection: {
    items: TagResItem[];
  };
  contentCtaCollection: {
    items: CtaResItem[];
  };
  navigationCtaCollection: {
    items: CtaResItem[];
  };
  bottomCtaCollection: {
    items: CtaResItem[];
  };
}

export interface ArticleRes {
  articleCollection: {
    items: ArticleResItem[];
  };
}

export interface QueryFilter {
  category?: string;
  tags?: string[];
  exclude?: string;
  limit?: number;
  skip?: number;
  slug?: string;
}

export class Article {
  id: string;
  slug: string;
  title: string;
  author: string;
  category: Category;
  tags: Tag[];
  published: string;
  formattedPublished: string;
  meta: {
    description: string;
    title: string;
  };
  primaryImage: string;
  primaryImageCaption?: string;
  primaryVideo: string;
  primaryVideoCaption?: string;
  content: string;
  shortOverview: string;
  readTime: number;
  contentCtas: Cta[];
  navigationCtas: Cta[];
  bottomCtas: Cta[];

  constructor(item: ArticleResItem) {
    this.id = item.sys.id;
    this.slug = item.slug;
    this.title = item.title;
    this.author = item.author;
    this.category = new Category(item.category);
    this.tags = item.tagCollection?.items.filter((t) => t && t.name).map((i) => new Tag(i));
    this.published = item.published;
    (this.formattedPublished = format(item.published, 'D MMMM YYYY')),
      (this.meta = {
        description: item.metaDescription,
        title: item.metaTitle,
      });
    this.primaryImage = item.primaryImage?.url || '';
    this.primaryImageCaption = item.primaryImageCaption;
    this.primaryVideo = item.primaryVideo;
    this.primaryVideoCaption = item.primaryVideoCaption;
    this.content = item.content;
    this.shortOverview = item.shortOverview;
    this.readTime = item.readTime;
    this.contentCtas = item.contentCtaCollection?.items.map((i) => new Cta(i));
    this.navigationCtas = item.navigationCtaCollection?.items.map((i) => new Cta(i));
    this.bottomCtas = item.bottomCtaCollection?.items.map((i) => new Cta(i));
  }

  static getQuery = ({ category, tags, exclude, limit, skip, slug }: QueryFilter) => {
    const categoryFilter = category ? `category: {slug: "${category}"},` : '';
    const tagsFilter = tags && tags.length > 0 ? `tag: {slug_in: [${tags?.map((t) => `"${t}"`)}]},` : '';
    const excludeFilter = exclude ? `sys: {id_not: "${exclude}"},` : '';
    const limitFilter = limit ? `limit: ${limit},` : '';
    const skipFilter = skip ? `skip: ${skip},` : '';
    const slugFilter = slug ? `slug: "${slug}",` : '';

    return `
      query {
        articleCollection(
          order: published_DESC,
          ${skipFilter}
          ${limitFilter}
          where:{
            ${categoryFilter}
            ${tagsFilter}
            ${excludeFilter}
            ${slugFilter}
          }
        ){
          items{
            ${ID_QUERY}
            slug
            title
            author
            published
            primaryImageCaption
            primaryVideo
            primaryVideoCaption
            content
            shortOverview
            readTime
            metaDescription
            metaTitle
            primaryImage{
              url
            }
            category{
              name
              slug
              ${ID_QUERY}
            }
            tagCollection{
              items{
                name
                ${ID_QUERY}
                description
                slug
              }
            }
            contentCtaCollection{
              items {
                ${CTA_ITEM_QUERY}
              }
            }
            navigationCtaCollection{
              items {
                ${CTA_ITEM_QUERY}
              }
            }
            bottomCtaCollection{
              items {
                ${CTA_ITEM_QUERY}
              }
            }
          }
        }
      }
    `;
  };
}
